"use client";

import React, { useState } from "react";
import SameDayDeliveryCard from "../same-day-delivery-card/same-day-delivery-card";
import chartData from "./opening-hours.json";
import { SendEvent } from '@monorepo-nx/std-watch/services/analytics';

// 50 states + DC + default mappings
const stateToTimeZone: Record<string, string> = {
  "Alabama": "America/Chicago",
  "Alaska": "America/Anchorage",
  "Arizona": "America/Phoenix",
  "Arkansas": "America/Chicago",
  "California": "America/Los_Angeles",
  "Colorado": "America/Denver",
  "Connecticut": "America/New_York",
  "Delaware": "America/New_York",
  "Florida": "America/New_York",
  "Georgia": "America/New_York",
  "Hawaii": "Pacific/Honolulu",
  "Idaho": "America/Boise",
  "Illinois": "America/Chicago",
  "Indiana": "America/Indiana/Indianapolis",
  "Iowa": "America/Chicago",
  "Kansas": "America/Chicago",
  "Kentucky": "America/New_York",
  "Louisiana": "America/Chicago",
  "Maine": "America/New_York",
  "Maryland": "America/New_York",
  "Massachusetts": "America/New_York",
  "Michigan": "America/Detroit",
  "Minnesota": "America/Chicago",
  "Mississippi": "America/Chicago",
  "Missouri": "America/Chicago",
  "Montana": "America/Denver",
  "Nebraska": "America/Chicago",
  "Nevada": "America/Los_Angeles",
  "New Hampshire": "America/New_York",
  "New Jersey": "America/New_York",
  "New Mexico": "America/Denver",
  "New York": "America/New_York",
  "North Carolina": "America/New_York",
  "North Dakota": "America/Chicago",
  "Ohio": "America/New_York",
  "Oklahoma": "America/Chicago",
  "Oregon": "America/Los_Angeles",
  "Pennsylvania": "America/New_York",
  "Rhode Island": "America/New_York",
  "South Carolina": "America/New_York",
  "South Dakota": "America/Chicago",
  "Tennessee": "America/Chicago",
  "Texas": "America/Chicago",
  "Utah": "America/Denver",
  "Vermont": "America/New_York",
  "Virginia": "America/New_York",
  "Washington": "America/Los_Angeles",
  "West Virginia": "America/New_York",
  "Wisconsin": "America/Chicago",
  "Wyoming": "America/Denver",
  "District of Columbia": "America/New_York"
};

const getSchedule = (advertiserName: string, state: string): any | null => {
  const normalizedKey = state.replace(/\s+/g, "").toLowerCase();
  const promotion = chartData.promotions.find(
    (p) => p.advertiser?.name === advertiserName
  );

  if (!promotion || !promotion.location) return null;

  const matchKey = Object.keys(promotion.location).find(
    (key) => key.toLowerCase() === normalizedKey
  );

  if (matchKey) return promotion.location[matchKey];
  if (promotion.location.usa) return promotion.location.usa;

  return null;
};

export function SamedayDeliveryChart({ chart }) {

  const timeZoneToState = Object.entries(stateToTimeZone).reduce((acc, [state, tz]) => {
    if (!acc[tz]) acc[tz] = state;
    return acc;
  }, {} as Record<string, string>);

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const defaultRecipientState = timeZoneToState[userTimeZone] || "New York";


  const [selectedRecipientState, setSelectedRecipientState] = useState(defaultRecipientState);

  const handleRecipientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRecipientState(e.target.value);
    SendEvent({ eventName: 'click-component', component: 'delivery-to-dropdown', value: e.target.value, text: e.target.value, href:'', section:'sameday delivery chart' })
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 pt-10">
        <div className="flex items-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold m-0 font-figtree">Same Day Flower Delivery</h2>
          <div className="ml-4 mt-3 relative text-sm font-figtree ">
            <label htmlFor="state" className="mr-2">
              Deliver to:
            </label>
            <select
              id="state"
              className="border border-gray-300 rounded px-2 py-1 cursor-pointer"
              value={selectedRecipientState}
              onChange={handleRecipientChange}
            >
              {Object.keys(stateToTimeZone).map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          🎯 Need flowers today? We’ve got you! <br />
          💐✨ Send stunning bouquets and timeless blooms – perfect for those last-minute surprises <br />
          📅 Ordering for a holiday? Beat the rush – order early!<br />
          😄🌸 Don't wait – make someone smile today! <br />
        </div>
      </div>

      {/* Cards */}
      {chart.promotions.map((promotion, index) => {
        const recipientTimeZone = stateToTimeZone[selectedRecipientState];
        const schedule = getSchedule(promotion.advertiser.name, selectedRecipientState);
        if (!schedule) return null;

        const today = new Date().toLocaleDateString("en-US", {
          weekday: "long",
          timeZone: recipientTimeZone,
        }).toLowerCase();

        const sectionKey = today === "saturday" ? "saturday" : today === "sunday" ? "sunday" : "weekdays";
        const timeSection = schedule[sectionKey];
        if (!timeSection) return null;

        return (
          <div key={index} className="py-5">
            <SameDayDeliveryCard
              promotion={promotion}
              score={promotion.score}
              senderTimeZone={userTimeZone}
              recipientTimeZone={recipientTimeZone}
              timeSection={timeSection}
            />
          </div>
        );
      })}
    </div>
  );
}

export default SamedayDeliveryChart;
